<script setup lang="ts">
import { ref, watchEffect, computed } from 'vue'
import { t, tc } from '@/locales/i18n'
import type { NewReportTableRow, ReportParams } from '@/types/api/reports'
import { usePaginatedFetcher } from '@/composables/fetchPaginated'
import GwInputNew from '@/components/lib/GwInputNew.vue'
import type { ApiError, ID } from '@/types/basic'
import GwDeleteDialog from '@/components/lib/GwDeleteDialog/GwDeleteDialog.vue'
import GwTransition from '@/components/lib/GwTransition/GwTransition.vue'
import GwBtn from '@/components/lib/GwBtn/GwBtn.vue'
import GwUiType from '@/components/lib/GwTable/GwUiType.vue'
import { CALL_DEBOUNCE_MS } from '@/helpers/constantsNew'
import {
  type GwTableCheckBoxState,
  newReportTableColumns,
} from '@/components/lib/GwTable/gwTableUtils'
import GwTableRow from '@/components/lib/GwTable/GwTableRow.vue'
import GwTable from '@/components/lib/GwTable/GwTable.vue'
import GwBox from '@/components/lib/GwBox/GwBox.vue'
import GwTableMessages from '@/components/lib/GwTable/GwTableMessages.vue'
import * as R from 'remeda'
import { getFetchCallBase, useFetch } from '@/composables/fetch'

interface EmitSelectType {
  headCheckBoxState: GwTableCheckBoxState
  selectedItems: ID[]
}

const emit = defineEmits<{
  'setActive:report': [id: ID]
  'create:report': []
  'delete:reports': []
  setError: [error: ApiError]
}>()

let reportsLimit = 50
const reportsCount = ref(0)
const checkboxState = ref<GwTableCheckBoxState | undefined>(undefined)
const selectedReports = ref<ID[]>([])
const searchInputValue = ref('')
const searchDebouncedValue = ref('')

const updateCount = (count: number) => {
  reportsCount.value = count
}

const { fetch } = useFetch()

const { fetchData, fetchNextData, state } = usePaginatedFetcher<NewReportTableRow, ReportParams>(
  'v2',
  'poker/hand-history/reports/',
  [],
  undefined,
  updateCount,
)

const errorCode = computed(() => state.error?.code)

const deleteReportsCall = ref(
  getFetchCallBase({
    apiVersion: 'v2',
    endpoint: '/poker/hand-history/reports/batch-update/',
    method: 'post',
  }),
)

const handleCheckboxStateUpdate = ({ headCheckBoxState, selectedItems }: EmitSelectType) => {
  checkboxState.value = headCheckBoxState
  selectedReports.value = selectedItems
}

const handleDeleteReports = async () => {
  state.data = state.data.filter(report => !selectedReports.value.includes(report.id))
  reportsCount.value -= selectedReports.value.length

  if (state.nextOffset) {
    state.nextOffset -= selectedReports.value.length
  }

  const entities = selectedReports.value
  deleteReportsCall.value.data = { is_saved: false, is_opened: false }
  deleteReportsCall.value.params = { entities }

  await fetch(deleteReportsCall.value)
  // Case when we also delete an active one and setActiveReport(nextActive)
  // needs to be called is not handled now.
  emit('delete:reports')
}

const fetchInitial = () =>
  fetchData({
    initial: true,
    params: { is_saved: true, search: searchDebouncedValue.value },
    limit: reportsLimit,
  })
defineExpose({ fetchInitial })

let timeoutResizedFetch: ReturnType<typeof setTimeout>
const handleLimitChange = (limit: number) => {
  reportsLimit = limit
  if (!state.data.length) {
    // intial call
    fetchInitial()
    return
  }
  clearTimeout(timeoutResizedFetch)
  timeoutResizedFetch = setTimeout(() => fetchInitial(), 1000)
}

watchEffect(() => {
  if (deleteReportsCall.value.error) {
    emit('setError', deleteReportsCall.value.error)
  }
})

const handleSearchDebounced = R.debounce(
  (value: string) => {
    searchDebouncedValue.value = value
    fetchInitial()
  },
  { waitMs: CALL_DEBOUNCE_MS },
)
const handleSearch = (value: string) => {
  searchInputValue.value = value
  handleSearchDebounced.call(value)
}

const shouldBeLoading = computed(() => searchDebouncedValue.value !== searchInputValue.value)
const loading = computed(() => shouldBeLoading.value || !!state.loading)
</script>

<template>
  <GwBox
    variant="box_table_default"
    style="
      padding: var(--app-pad-6) var(--app-pad-6) 0 var(--app-pad-6);

      border-radius: var(--app-rad-4);
      overflow: hidden;
    "
    class="flex-1 d-flex mh-0 mw-0 position-relative"
  >
    <div
      style="max-width: clamp(1px, 54rem, 100%); gap: var(--app-gap-6)"
      class="mx-auto f-column flex-1"
    >
      <div
        class="box-hacked-back f-column flex-1"
        style="position: absolute; left: 0; top: 0; height: 100%; width: 100%"
      >
        <div
          class="box-hacked-back_full-clr"
          style="background: #1e1e1e; height: 9rem; width: 100%"
        />
        <div
          class="box-hacked-back_linear-gradient flex-1"
          style="
            background: linear-gradient(180deg, #1e1e1e 0%, hsla(0, 0%, 0%, 0.2) 100%);
            width: 100%;
          "
        />
      </div>
      <div
        class="f-center"
        style="gap: var(--app-gap-2)"
      >
        <GwInputNew
          :placeholder="t('hhv3.find_report')"
          @update:value="handleSearch"
        />
        <div class="d-flex ml-auto">
          <GwBtn
            data-tst="create_new_report"
            icon="mdi-text-box-plus-outline"
            variant="btn_primary-v2"
            style="--gwbtn-clr: var(--clr-primary)"
            @click="emit('create:report')"
          >
            {{ t('hhv3.create_new_report') }}
          </GwBtn>
        </div>
      </div>

      <GwTable
        hasCheckboxes
        variant="table_fat"
        idKey="id"
        :items="state.error ? [] : state.data"
        :error="state.error"
        :loading
        :columns="newReportTableColumns"
        :hasPagination="false"
        style="--clr-loader-back: transparent"
        @scrolled:end="fetchNextData"
        @update:renderedItems="handleLimitChange"
        @update:headCheckboxState="handleCheckboxStateUpdate"
      >
        <template #before>
          <GwTransition
            variant="slidebottom_fade"
            appear
          >
            <div
              v-if="selectedReports.length && !state.loading"
              class="f-center w-100"
              style="
                position: fixed;
                bottom: calc(
                  var(--app-safe-area-inset-bottom) + var(--app-footer-height) + var(--app-pad-5)
                );
                left: 0;
                z-index: 8;
              "
            >
              <GwBox variant="box_menu">
                <GwDeleteDialog
                  data-tst="open_delete_dialog"
                  :btnOpenerText="t('hhv3.delete_reports_dialog.title')"
                  :modalTitle="t('hhv3.delete_reports_dialog.title')"
                  :modalText="tc('hhv3.delete_reports_dialog.text', selectedReports.length)"
                  :btnDeleteText="
                    tc('hhv3.delete_reports_dialog.delete_text', selectedReports.length)
                  "
                  @delete="handleDeleteReports"
                />
              </GwBox>
            </div>
          </GwTransition>
        </template>

        <template #error>
          <GwTableMessages
            class="anim-fade-in"
            message="error"
            :hasRefreshBtn="false"
            :errorCode
          />
        </template>
        <template #no_data>
          <GwTableMessages
            class="anim-fade-in"
            message="search_no_reports"
          />
        </template>

        <template #row="{ item, index, methods, selectedItems, variantConf }">
          <GwTableRow
            :id="item.id"
            :data-tst="`saved_report_${index}`"
            hasCheckbox
            :selectedItems
            :variantConf
            style="color: var(--app-clr-grey-2); font-weight: 500"
            @click:checkbox="methods.selectItem"
          >
            <td
              v-for="column in newReportTableColumns"
              :key="column.id"
              style="cursor: pointer"
              @click.stop="emit('setActive:report', item.id)"
            >
              <GwUiType
                :value="item[column.field]"
                :uiType="column.uiType"
              />
            </td>
          </GwTableRow>
        </template>
      </GwTable>
    </div>
  </GwBox>
</template>
